import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Devices,
  CheckCircle,
  ExpandMore as ExpandMoreIcon,
  PhoneAndroid,
  Computer,
  TabletMac,
  DevicesOther,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { authService } from "../../services/authService";
import { formatDateTime } from "../../utils/dateUtils";

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
    <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", width: 120, flexShrink: 0 }}>
      {label}
    </Typography>
    {typeof value === "string" ? (
      <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.8)" }}>{value || "-"}</Typography>
    ) : value}
  </Box>
);

const SessionSecurityPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    loadSessionData();
    // Recargar cada 2 minutos
    const interval = setInterval(loadSessionData, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      setError("");

      const [sessionsData, devicesData] = await Promise.all([
        authService.getActiveSessions(),
        authService.getRegisteredDevices(),
      ]);

      if (sessionsData) {
        setSessions(sessionsData.sessions || []);
      }
      if (devicesData) {
        setDevices(devicesData.devices || []);
      }
    } catch (err) {
      setError("Error cargando información de sesiones");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Usar utilidad centralizada de fechas con zona horaria CDMX
  const formatDate = formatDateTime;

  const getDeviceIcon = (type) => {
    if (type === "mobile") return <PhoneAndroid sx={{ fontSize: 20 }} />;
    if (type === "tablet") return <TabletMac sx={{ fontSize: 20 }} />;
    if (type === "desktop") return <Computer sx={{ fontSize: 20 }} />;
    return <DevicesOther sx={{ fontSize: 20 }} />;
  };

  const [expandedSession, setExpandedSession] = useState(null);
  const [expandedDevice, setExpandedDevice]   = useState(null);
  const [devicesPage, setDevicesPage]         = useState(0);
  const DEVICES_PER_PAGE = 5;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* SESIONES ACTIVAS */}
      <Card sx={{ mb: 3, background: "rgba(25,118,210,0.1)", border: "1px solid #1976d2" }}>
        <CardHeader
          avatar={<CheckCircle sx={{ color: "success.main" }} />}
          title="🔐 Sesiones Activas"
          subheader={`${sessions.length} sesión${sessions.length !== 1 ? "es" : ""} activa${sessions.length !== 1 ? "s" : ""}`}
        />
        <CardContent sx={{ pt: 0 }}>
          {sessions.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No hay sesiones activas</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {sessions.map((session) => {
                const isOpen = expandedSession === session.id;
                return (
                  <Paper
                    key={session.id}
                    sx={{
                      borderRadius: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${isOpen ? "rgba(25,118,210,0.5)" : "rgba(255,255,255,0.08)"}`,
                      overflow: "hidden",
                      transition: "border-color .2s",
                    }}
                  >
                    {/* Fila visible */}
                    <Box
                      onClick={() => setExpandedSession(isOpen ? null : session.id)}
                      sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5, cursor: "pointer", gap: 1.5 }}
                    >
                      <Box sx={{ color: "primary.main", display: "flex", flexShrink: 0 }}>
                        {getDeviceIcon(session.device_type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff" }} noWrap>
                          {session.device_name}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)" }} noWrap>
                          {formatDate(session.last_activity)}
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ color: "rgba(255,255,255,0.4)", flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .25s" }}>
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {/* Panel expandido */}
                    <Collapse in={isOpen}>
                      <Divider sx={{ borderColor: "rgba(255,255,255,0.07)" }} />
                      <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                        <InfoRow label="Tipo" value={<Chip label={session.device_type} size="small" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />} />
                        <InfoRow label="Navegador" value={session.browser} />
                        <InfoRow label="Sistema Operativo" value={session.os} />
                        <InfoRow label="Conectado" value={formatDate(session.fecha_inicio || session.login_timestamp)} />
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* DISPOSITIVOS REGISTRADOS */}
      <Card sx={{ background: "rgba(56,142,60,0.1)", border: "1px solid #388e3c" }}>
        <CardHeader
          avatar={<Devices sx={{ color: "success.main" }} />}
          title="📱 Dispositivos Registrados"
          subheader={`${devices.length} dispositivo${devices.length !== 1 ? "s" : ""} conocido${devices.length !== 1 ? "s" : ""}`}
        />
        <CardContent sx={{ pt: 0 }}>
          {devices.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No hay dispositivos registrados</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {devices.slice(devicesPage * DEVICES_PER_PAGE, devicesPage * DEVICES_PER_PAGE + DEVICES_PER_PAGE).map((device) => {
                const isOpen = expandedDevice === device.id;
                return (
                  <Paper
                    key={device.id}
                    sx={{
                      borderRadius: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${isOpen ? "rgba(56,142,60,0.5)" : "rgba(255,255,255,0.08)"}`,
                      overflow: "hidden",
                      transition: "border-color .2s",
                    }}
                  >
                    {/* Fila visible */}
                    <Box
                      onClick={() => setExpandedDevice(isOpen ? null : device.id)}
                      sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5, cursor: "pointer", gap: 1.5 }}
                    >
                      <Box sx={{ color: "success.main", display: "flex", flexShrink: 0 }}>
                        {getDeviceIcon(device.device_type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff" }} noWrap>
                          {device.device_name}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)" }} noWrap>
                          {formatDate(device.last_seen)}
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ color: "rgba(255,255,255,0.4)", flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .25s" }}>
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {/* Panel expandido */}
                    <Collapse in={isOpen}>
                      <Divider sx={{ borderColor: "rgba(255,255,255,0.07)" }} />
                      <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                        <InfoRow label="Navegador" value={device.browser} />
                        <InfoRow label="Sistema Operativo" value={device.os} />
                        <InfoRow label="Dirección IP" value={<Typography sx={{ fontFamily: "monospace", fontSize: "0.82rem", color: "rgba(255,255,255,0.8)" }}>{device.ip_address}</Typography>} />
                        <InfoRow label="Confiable" value={<Chip label={device.is_trusted ? "✓ Confiable" : "No confiable"} size="small" color={device.is_trusted ? "success" : "default"} variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />} />
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
              {/* Paginación */}
              {devices.length > DEVICES_PER_PAGE && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, pt: 1 }}>
                  <IconButton
                    size="small"
                    disabled={devicesPage === 0}
                    onClick={() => setDevicesPage(p => p - 1)}
                    sx={{ color: devicesPage === 0 ? "rgba(255,255,255,0.2)" : "success.main" }}
                  >
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", minWidth: 80, textAlign: "center" }}>
                    {devicesPage * DEVICES_PER_PAGE + 1}–{Math.min((devicesPage + 1) * DEVICES_PER_PAGE, devices.length)} de {devices.length}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={(devicesPage + 1) * DEVICES_PER_PAGE >= devices.length}
                    onClick={() => setDevicesPage(p => p + 1)}
                    sx={{ color: (devicesPage + 1) * DEVICES_PER_PAGE >= devices.length ? "rgba(255,255,255,0.2)" : "success.main" }}
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>🔒 Política de Seguridad:</strong>
        <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
          <li>Solo puedes tener una sesión activa a la vez.</li>
          <li>Si inicias sesión desde otro dispositivo, la sesión anterior se cerrará automáticamente.</li>
          <li>Los dispositivos que uses se registran automáticamente para tu referencia.</li>
        </Box>
      </Alert>
    </Box>
  );
};

export default SessionSecurityPanel;
